/**
 * Shared action row for the design-data forms (FixDesignDataForm, PrintDataForm).
 *
 * DOM order Cancel, save-for-upload, save-and-resolve: primary LAST and purple
 * = the action that closes the issue (save + verify). Saving without resolving
 * is the secondary step. The two long-label buttons wrap their label
 * (`h-auto min-h-12 whitespace-normal text-center`) so a narrow dialog never
 * scrolls sideways to show them (DESIGN.md §22, PRODUCTION_DESIGN.md §6.3).
 * While a save runs every action is disabled and the spinner shows in the
 * button that was pressed (DESIGN.md §15.1).
 */
import { useTranslation } from 'react-i18next';
import { Loader2, Save, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';

export type DesignDataSubmitMode = 'save_for_upload' | 'save_and_resolve';

interface DesignDataFormFooterProps {
  /** The mode whose button was pressed while its save is in flight; null when idle. */
  savingMode: DesignDataSubmitMode | null;
  submitDisabled: boolean;
  onCancel: () => void;
  onSubmit: (mode: DesignDataSubmitMode) => void;
}

// `[&_svg]:size-5` beats Button's `[&_svg]:size-4` (an icon's own h-5 w-5 does not).
const LONG_LABEL_BUTTON = 'h-auto min-h-12 gap-1.5 whitespace-normal text-center text-base [&_svg]:size-5';

export function DesignDataFormFooter({
  savingMode,
  submitDisabled,
  onCancel,
  onSubmit,
}: DesignDataFormFooterProps) {
  const { t } = useTranslation('production');
  const isSaving = savingMode !== null;
  const spinner = <Loader2 className="motion-safe:animate-spin" aria-hidden="true" />;
  return (
    <DialogFooter>
      <Button className="h-12 text-base" variant="outline" onClick={onCancel} disabled={isSaving}>
        {t('issues.resolve.cancelBtn')}
      </Button>
      <Button
        className={LONG_LABEL_BUTTON}
        variant="outline"
        onClick={() => onSubmit('save_for_upload')}
        disabled={submitDisabled || isSaving}
      >
        {savingMode === 'save_for_upload' ? spinner : <Save aria-hidden="true" />}
        {t('issues.printFiles.upload.saveForUpload')}
      </Button>
      <Button
        className={`${LONG_LABEL_BUTTON} bg-threadheads-purple text-white hover:bg-threadheads-purple/90`}
        disabled={submitDisabled || isSaving}
        onClick={() => onSubmit('save_and_resolve')}
      >
        {savingMode === 'save_and_resolve' ? spinner : <CheckCircle aria-hidden="true" />}
        {t('issues.printFiles.upload.saveAndResolve')}
      </Button>
    </DialogFooter>
  );
}
